import { useState } from 'react'
import { useNavigate } from 'react-router'
import { Check, ChevronDown, ChevronUp } from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { useSubscription, useCheckout } from '@/hooks/useSubscription'
import { usePricing } from '@/hooks/usePricing'
import type { PricingTier } from '@/types/subscription'

interface FAQItem {
  question: string
  answer: string
}

const faqs: FAQItem[] = [
  {
    question: 'Can I change my plan at any time?',
    answer:
      "Yes, you can upgrade or downgrade your plan at any time. When upgrading, you'll be charged the prorated difference for the remainder of your billing cycle. When downgrading, the new rate will apply at the start of your next billing cycle.",
  },
  {
    question: 'What happens when my trial ends?',
    answer:
      "At the end of your 1-month free trial, you'll enter read-only mode and can still access all your existing data. Upgrade anytime to resume creating recordings and adding guests.",
  },
  {
    question: 'Do you offer refunds?',
    answer:
      "We offer a 30-day money-back guarantee on all plans. If you're not satisfied with Floaty within the first 30 days of your paid subscription, contact our support team for a full refund.",
  },
]

/**
 * Stripe-integrated pricing page that fetches tiers from Stripe
 * and handles checkout flow
 */
export function StripePricingPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { subscription } = useSubscription()
  const { tiers, isLoading } = usePricing()
  const checkout = useCheckout()
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null)

  const toggleFAQ = (index: number) => {
    setExpandedFAQ(expandedFAQ === index ? null : index)
  }

  const handleSelectPlan = (tier: PricingTier) => {
    // Free trial - just sign up or go to dashboard
    if (tier.id === 'free_trial') {
      if (user) {
        navigate('/pipeline')
      } else {
        navigate('/signup')
      }
      return
    }

    // Paid plan - need to be logged in
    if (!user) {
      navigate(`/signup?plan=${tier.id}`)
      return
    }

    // Start checkout
    checkout.mutate(tier.id)
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 flex items-center justify-center">
        <div className="text-zinc-500 dark:text-zinc-400">Loading pricing...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-zinc-900 dark:text-zinc-100 mb-4">
            Simple, transparent pricing
          </h1>
          <p className="text-lg text-zinc-600 dark:text-zinc-400 max-w-2xl mx-auto">
            Start free for 1 month. Upgrade when you're ready.
          </p>
        </div>

        {/* Trial info banner */}
        <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-8 text-center">
          <p className="text-green-800 dark:text-green-200">
            <span className="font-semibold">
              All plans include a 1-month free trial
            </span>{' '}
            &mdash; 4 recordings &amp; 8 guests, no credit card required
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {tiers.map((tier) => {
            const isCurrentPlan =
              (tier.id === 'free_trial' && subscription?.status === 'trial') ||
              tier.id === subscription?.price_id

            const isFreeTier = tier.id === 'free_trial'

            return (
              <div
                key={tier.id}
                className={`
                  relative bg-white dark:bg-zinc-900 rounded-2xl shadow-sm border-2 transition-all hover:shadow-lg
                  ${
                    isCurrentPlan
                      ? 'border-blue-500 ring-2 ring-blue-500/20'
                      : 'border-zinc-200 dark:border-zinc-800'
                  }
                `}
              >
                {isCurrentPlan && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-blue-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      Current Plan
                    </span>
                  </div>
                )}

                <div className="p-6">
                  {/* Plan Name */}
                  <h3 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
                    {tier.name}
                  </h3>

                  {/* Price */}
                  <div className="mt-4 mb-6">
                    {tier.price === 0 ? (
                      <span className="text-4xl font-bold text-zinc-900 dark:text-zinc-100">
                        Free
                      </span>
                    ) : (
                      <>
                        <span className="text-4xl font-bold text-zinc-900 dark:text-zinc-100">
                          ${(tier.price || 0) / 100}
                        </span>
                        <span className="text-zinc-500 dark:text-zinc-400">
                          /{tier.interval}
                        </span>
                      </>
                    )}
                  </div>

                  {/* Description */}
                  {tier.description && (
                    <p className="text-zinc-600 dark:text-zinc-400 text-sm mb-6">
                      {tier.description}
                    </p>
                  )}

                  {/* Features */}
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-center text-sm text-zinc-600 dark:text-zinc-400">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mr-2">
                        <Check className="w-3 h-3 text-green-600 dark:text-green-400" />
                      </div>
                      <span>
                        {tier.metadata.recordings
                          ? `${tier.metadata.recordings} recordings`
                          : 'Unlimited recordings'}
                      </span>
                    </li>
                    <li className="flex items-center text-sm text-zinc-600 dark:text-zinc-400">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mr-2">
                        <Check className="w-3 h-3 text-green-600 dark:text-green-400" />
                      </div>
                      <span>
                        {tier.metadata.guests
                          ? `${tier.metadata.guests} guests`
                          : 'Unlimited guests'}
                      </span>
                    </li>
                  </ul>

                  {/* CTA Button */}
                  <button
                    onClick={() => handleSelectPlan(tier)}
                    disabled={isCurrentPlan || checkout.isPending}
                    className={`
                      w-full py-3 px-4 rounded-lg font-semibold transition-all
                      ${
                        isCurrentPlan
                          ? 'bg-zinc-100 dark:bg-zinc-800 text-zinc-400 dark:text-zinc-500 cursor-not-allowed'
                          : isFreeTier
                          ? 'bg-zinc-900 dark:bg-zinc-100 hover:bg-zinc-800 dark:hover:bg-zinc-200 text-white dark:text-zinc-900'
                          : 'bg-blue-600 hover:bg-blue-700 text-white'
                      }
                      disabled:opacity-50
                    `}
                  >
                    {isCurrentPlan
                      ? 'Current Plan'
                      : checkout.isPending
                      ? 'Loading...'
                      : isFreeTier
                      ? 'Get Started'
                      : 'Upgrade'}
                  </button>
                </div>
              </div>
            )
          })}
        </div>

        {/* FAQ Section */}
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100 text-center mb-8">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 overflow-hidden"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors"
                >
                  <span className="font-medium text-zinc-900 dark:text-zinc-100">
                    {faq.question}
                  </span>
                  {expandedFAQ === index ? (
                    <ChevronUp className="w-5 h-5 text-zinc-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-zinc-400 flex-shrink-0" />
                  )}
                </button>
                {expandedFAQ === index && (
                  <div className="px-6 pb-4">
                    <p className="text-zinc-600 dark:text-zinc-400">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
